import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { useAuth } from "./AuthContext";
import { useTenantContext } from "./TenantContext";
import { locationApi } from "../services/api";

const LocationContext = createContext(null);

const CURRENT_LOCATION_KEY = "rocket_tronc_current_location";

/**
 * Extract data from API response (handles both casing conventions)
 */
const extractData = (response) => {
  if (!response) return { success: false, data: null };
  const success = response.success ?? response.Success ?? false;
  const data = response.data ?? response.Data ?? null;
  return { success, data };
};

export const LocationProvider = ({ children }) => {
  const { user, isAuthenticated, token, loading: authLoading } = useAuth();
  const { currentTenantId } = useTenantContext();

  const [locations, setLocations] = useState([]);
  const [currentLocation, setCurrentLocationState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Track tenant changes to clear stale location
  const [prevTenantId, setPrevTenantId] = useState(currentTenantId);

  // Load locations when user is authenticated AND token is available
  // Also reload when tenant changes (for Global Admins)
  useEffect(() => {
    console.log("[LocationContext] Effect triggered:", {
      authLoading,
      isAuthenticated,
      hasUser: !!user,
      hasToken: !!token,
      currentTenantId,
    });

    // Wait for auth to finish loading
    if (authLoading) {
      console.log("[LocationContext] Auth still loading, waiting...");
      return;
    }

    // Detect tenant change and clear stale location
    if (currentTenantId !== prevTenantId) {
      console.log(
        "[LocationContext] Tenant changed from",
        prevTenantId,
        "to",
        currentTenantId,
        "- clearing stale location",
      );
      setPrevTenantId(currentTenantId);
      setCurrentLocationState(null);
      localStorage.removeItem(CURRENT_LOCATION_KEY);
    }

    // Only load if we have both authentication and a token
    if (isAuthenticated && user && token) {
      console.log("[LocationContext] Loading locations for user:", user.email);
      // Small delay to ensure token is properly set in localStorage
      const timeoutId = setTimeout(() => {
        loadLocations();
      }, 100);
      return () => clearTimeout(timeoutId);
    } else {
      console.log(
        "[LocationContext] Not ready to load locations, clearing state",
      );
      setLocations([]);
      setCurrentLocationState(null);
      setLoading(false);
    }
  }, [isAuthenticated, user?.id, token, currentTenantId, authLoading]);

  // Restore current location from localStorage on mount, or auto-select first location
  useEffect(() => {
    if (locations.length > 0) {
      const storedLocationId = localStorage.getItem(CURRENT_LOCATION_KEY);
      if (storedLocationId) {
        const storedLocation = locations.find((l) => l.id === storedLocationId);
        if (storedLocation) {
          console.log(
            "[LocationContext] Restored location from localStorage:",
            storedLocation.name,
          );
          setCurrentLocationState(storedLocation);
          return;
        }
      }
      // Stored location not found (e.g. tenant switched) or no stored location
      // Auto-select the first location
      console.log(
        "[LocationContext] Auto-selecting first location:",
        locations[0].name,
      );
      setCurrentLocation(locations[0]);
    }
  }, [locations]);

  const loadLocations = useCallback(async () => {
    console.log(
      "[LocationContext] Loading locations, tenantId:",
      currentTenantId,
    );
    setLoading(true);
    setError(null);

    try {
      // Pass currentTenantId to the API if available (for Global Admins)
      const response = await locationApi.getDropdown(currentTenantId);
      const { success, data } = extractData(response);

      if (success && data) {
        console.log("[LocationContext] Loaded locations:", data.length);
        setLocations(data);
      } else {
        console.log("[LocationContext] No locations returned");
        setLocations([]);
      }
    } catch (err) {
      console.error("[LocationContext] Failed to load locations:", err);

      // Only set error if it's not an auth error (401)
      // Auth errors are handled by the API client
      if (err.status !== 401) {
        setError(err.message || "Failed to load locations");
      }
      setLocations([]);
    } finally {
      setLoading(false);
    }
  }, [currentTenantId]);

  const setCurrentLocation = useCallback((location) => {
    console.log(
      "[LocationContext] Setting current location:",
      location?.name || "null",
    );
    setCurrentLocationState(location);
    if (location) {
      localStorage.setItem(CURRENT_LOCATION_KEY, location.id);
    } else {
      localStorage.removeItem(CURRENT_LOCATION_KEY);
    }
  }, []);

  const clearCurrentLocation = useCallback(() => {
    console.log("[LocationContext] Clearing current location");
    setCurrentLocationState(null);
    localStorage.removeItem(CURRENT_LOCATION_KEY);
  }, []);

  const refreshLocations = useCallback(async () => {
    console.log("[LocationContext] Refreshing locations...");
    await loadLocations();
  }, [loadLocations]);

  // Check if user needs to select a location
  const needsLocationSelection =
    !loading && locations.length > 1 && !currentLocation;

  // Check if user has access to multiple locations
  const hasMultipleLocations = locations.length > 1;

  // Check if user has any locations
  const hasLocations = locations.length > 0;

  // For TenantAdmin/GlobalAdmin, they can see all locations without needing explicit access
  const { isGlobalAdmin, isTenantAdmin } = useAuth();
  const canAccessAllLocations = isGlobalAdmin || isTenantAdmin;

  // Get current location ID (for API calls)
  const currentLocationId = currentLocation?.id ?? null;

  const value = {
    // State
    locations,
    currentLocation,
    currentLocationId,
    loading,
    error,

    // Computed
    needsLocationSelection,
    hasMultipleLocations,
    hasLocations,
    canAccessAllLocations,

    // Actions
    setCurrentLocation,
    clearCurrentLocation,
    refreshLocations,
  };

  return (
    <LocationContext.Provider value={value}>
      {children}
    </LocationContext.Provider>
  );
};

/**
 * Hook to access location context
 * Named useLocationContext to avoid conflicts with react-router-dom's useLocation
 */
export const useLocationContext = () => {
  const context = useContext(LocationContext);
  if (!context) {
    throw new Error(
      "useLocationContext must be used within a LocationProvider",
    );
  }
  return context;
};

// Also export as useLocation for backwards compatibility, but prefer useLocationContext
export const useLocation = useLocationContext;

export default LocationContext;
